import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Backdrop from "@mui/material/Backdrop";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { IoChevronBack, IoChevronForward, IoCheckmarkCircle, IoLockClosedOutline, IoMailOutline, IoCallOutline, IoWarningOutline, IoClose } from "react-icons/io5";
import Header from "./Header";
import AlertMessage from "../../common/components/AlertMessage";
import FamilyOtpVerifyDialog, { OTP_MODE_EDIT_MOBILE } from "../familymember/FamilyOtpVerifyDialog";
import { OTP_ACTION_SEND, OTP_PURPOSE_EMAIL_VERIFICATION, OTP_ROLE_CAREGIVER, sendEmailOtp, sendMobileOtp } from "../../common/api/familyportalOtpApi";
import { getProfileDetails, PROFILE_ROLE_CAREGIVER, sendEditMobileOtp } from "../../common/api/profileApi";
import { getStoredUserEmail } from "../../common/utils/sessionContext";
import { formatProfileMobile, formatEditMobileInput, getProfileInitials } from "../../common/utils/familyAccountSettings";
import {
  RESEND_LOCK_MS,
  formatMobileSendLockMessage,
  isSendOtpRateLimitError,
  parseLockedUntilMs,
  readPersistedEmailVerifyResendLock,
  readPersistedMobileResendLock,
  resolveEmailVerifyLockEndsAt,
  writePersistedEmailVerifyResendLock,
  writePersistedMobileResendLock,
  clearPersistedEmailVerifyResendLock,
  clearPersistedMobileResendLock,
} from "../../common/utils/forgotPasswordResendLock";
import { CONTACT_VERIFICATION_ROLE, markContactVerified, syncContactVerificationFromProfile } from "../../common/utils/contactVerification";
import * as Sentry from "@sentry/react";
import "../../styles/familymember.css";
import "../../styles/caregiver/profile.css";

const mapApiCaregiverProfileToState = (data = {}) => {
  const fullName = String(data.fullName || "").trim();
  const parts = fullName.split(/\s+/).filter(Boolean);
  const firstName = parts[0] || "";
  const lastName = parts.slice(1).join(" ") || "";

  return {
    fullName,
    firstName,
    lastName,
    email: String(data.emailId || "").trim(),
    mobile: String(data.mobileNumber || "").trim(),
    emailVerified: Boolean(data.isEmailVerified),
    mobileVerified: Boolean(data.isMobileVerified),
    hasEmail: Boolean(data.emailId),
    hasMobile: Boolean(data.mobileNumber),
  };
};

const getCaregiverAccountSettings = () => {
  const fullName = localStorage.getItem("caregiverName") || "";
  const parts = fullName.split(/\s+/).filter(Boolean);
  return {
    fullName,
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" ") || "",
    email: "",
    mobile: "",
    emailVerified: false,
    mobileVerified: false,
    hasEmail: false,
    hasMobile: false,
  };
};

const ProfileHero = ({ profile }) => {
  const displayName = profile.fullName || [profile.firstName, profile.lastName].filter(Boolean).join(" ") || "Caregiver";
  const initials = getProfileInitials(profile.firstName, profile.lastName);

  return (
    <header className="caregiver-profile-hero">
      <div className="caregiver-profile-hero-avatar" aria-hidden="true">
        {initials}
      </div>
      <h2 className="caregiver-profile-hero-name">{displayName}</h2>
    </header>
  );
};

const ContactFormField = ({ id, label, icon: Icon, value, isVerified, hasValue, onVerify, onEdit, verifyDisabled = false, isVerifying = false, fieldWarning = "" }) => (
  <div className="family-profile-field family-profile-field--contact">
    <div className="family-profile-field-head">
      <label htmlFor={id} className="family-profile-field-label">
        {Icon ? (
          <span className="family-profile-contact-icon" aria-hidden="true">
            <Icon />
          </span>
        ) : null}
        {label}
      </label>
      <div className="family-profile-field-actions">
        {isVerified ? (
          <span className="family-profile-field-status family-profile-field-status--ok" aria-label="Verified">
            <IoCheckmarkCircle aria-hidden="true" /> Verified
          </span>
        ) : (
          <>
            {hasValue && onVerify ? (
              <button type="button" className="family-profile-field-action family-profile-field-action--primary" onClick={onVerify} disabled={verifyDisabled || isVerifying}>
                {isVerifying ? "Sending…" : "Verify"}
              </button>
            ) : null}
            {onEdit ? (
              <button type="button" className="family-profile-field-action" onClick={onEdit}>
                Edit
                <IoChevronForward aria-hidden="true" />
              </button>
            ) : null}
          </>
        )}
      </div>
    </div>
    <input id={id} type="text" className="family-profile-field-input" value={value} readOnly />
    {fieldWarning ? (
      <p className="family-profile-field-warning" role="alert">
        <IoWarningOutline aria-hidden="true" />
        {fieldWarning}
      </p>
    ) : null}
  </div>
);

const CaregiverProfile = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(() => getCaregiverAccountSettings());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [verifyTarget, setVerifyTarget] = useState(null);
  const [mobileOtpMeta, setMobileOtpMeta] = useState(null);
  const [emailOtpMeta, setEmailOtpMeta] = useState(null);
  const [isSendingMobileOtp, setIsSendingMobileOtp] = useState(false);
  const [isSendingEmailOtp, setIsSendingEmailOtp] = useState(false);
  const [mobileSendError, setMobileSendError] = useState("");
  const [emailSendError, setEmailSendError] = useState("");
  const [mobileSendLockEndsAt, setMobileSendLockEndsAt] = useState(null);
  const [emailSendLockEndsAt, setEmailSendLockEndsAt] = useState(null);
  const [mobileSendLockSeconds, setMobileSendLockSeconds] = useState(0);
  const [emailSendLockSeconds, setEmailSendLockSeconds] = useState(0);
  const [verifyToast, setVerifyToast] = useState("");
  const [editMobileOpen, setEditMobileOpen] = useState(false);
  const [editMobileValue, setEditMobileValue] = useState("");
  const [editMobileError, setEditMobileError] = useState("");
  const [isSubmittingEditMobile, setIsSubmittingEditMobile] = useState(false);
  const [pendingEditMobile, setPendingEditMobile] = useState("");

  const isMobileSendLocked = Boolean(mobileSendLockEndsAt && mobileSendLockEndsAt > Date.now());
  const displayedMobileSendLockSeconds = mobileSendLockSeconds > 0 ? mobileSendLockSeconds : mobileSendLockEndsAt ? Math.max(0, Math.ceil((mobileSendLockEndsAt - Date.now()) / 1000)) : 0;
  const mobileSendLockMessage = isMobileSendLocked ? formatMobileSendLockMessage(displayedMobileSendLockSeconds) : "";

  const isEmailSendLocked = Boolean(emailSendLockEndsAt && emailSendLockEndsAt > Date.now());
  const displayedEmailSendLockSeconds = emailSendLockSeconds > 0 ? emailSendLockSeconds : emailSendLockEndsAt ? Math.max(0, Math.ceil((emailSendLockEndsAt - Date.now()) / 1000)) : 0;
  const emailSendLockMessage = isEmailSendLocked ? formatMobileSendLockMessage(displayedEmailSendLockSeconds) : "";

  const applyMobileSendLock = (lockEndsAt) => {
    if (!lockEndsAt) {
      return;
    }
    setMobileSendLockEndsAt(lockEndsAt);
    writePersistedMobileResendLock(profile.mobile, lockEndsAt);
  };

  const applyEmailSendLock = (lockEndsAt) => {
    if (!lockEndsAt) {
      return;
    }
    setEmailSendLockEndsAt(lockEndsAt);
    writePersistedEmailVerifyResendLock(profile.email, lockEndsAt);
  };

  const handleEmailVerify = async () => {
    if (!profile.email || isEmailSendLocked || isSendingEmailOtp) {
      return;
    }

    setIsSendingEmailOtp(true);
    setEmailSendError("");
    const result = await sendEmailOtp({
      email: profile.email,
      purpose: OTP_PURPOSE_EMAIL_VERIFICATION,
      action: OTP_ACTION_SEND,
      role: OTP_ROLE_CAREGIVER,
    });
    setIsSendingEmailOtp(false);

    if (result.success) {
      setEmailOtpMeta(result.data);
      setVerifyTarget("email");
      return;
    }

    if (isSendOtpRateLimitError(result.data)) {
      const lockEndsAt = resolveEmailVerifyLockEndsAt({
        email: profile.email,
        lockedUntil: result.data?.lockedUntil,
        attemptsRemaining: result.data?.attemptsRemaining ?? 0,
      });
      applyEmailSendLock(lockEndsAt);
      return;
    }

    setEmailSendError(result.message || "Failed to send verification code.");
  };

  const handleMobileVerify = async () => {
    if (!profile.mobile || isMobileSendLocked || isSendingMobileOtp) {
      return;
    }

    setPendingEditMobile("");
    setIsSendingMobileOtp(true);
    setMobileSendError("");
    try {
      const result = await sendMobileOtp({
        mobileNumber: profile.mobile,
        role: OTP_ROLE_CAREGIVER,
      });

      if (result.success) {
        setMobileOtpMeta(result.data);
        setVerifyTarget("mobile");
        return;
      }

      if (isSendOtpRateLimitError(result.data)) {
        const lockEndsAt = parseLockedUntilMs(result.data?.lockedUntil) || (result.data?.attemptsRemaining === 0 ? Date.now() + RESEND_LOCK_MS : null);
        applyMobileSendLock(lockEndsAt);
        return;
      }

      setMobileSendError(result.message || "Failed to send verification code.");
    } catch (error) {
      Sentry.captureException(error);
      setMobileSendError("Failed to send verification code.");
    } finally {
      setIsSendingMobileOtp(false);
    }
  };

  const openEditMobile = () => {
    setEditMobileValue(formatEditMobileInput(profile.mobile));
    setEditMobileError("");
    setEditMobileOpen(true);
  };

  const closeEditMobile = () => {
    if (isSubmittingEditMobile) {
      return;
    }
    setEditMobileOpen(false);
    setEditMobileValue("");
    setEditMobileError("");
  };

  const handleEditMobileSubmit = async (event) => {
    event.preventDefault();
    const formattedMobile = formatEditMobileInput(editMobileValue);
    const digits = formattedMobile.replace(/\D/g, "");

    if (!digits) {
      setEditMobileError("Please enter a mobile number.");
      return;
    }

    if (digits.length !== 10) {
      setEditMobileError("Please enter a valid 10-digit mobile number.");
      return;
    }

    const currentDigits = String(profile.mobile || "").replace(/\D/g, "");
    const currentLocal = currentDigits.length === 11 && currentDigits.startsWith("1") ? currentDigits.slice(1) : currentDigits;
    if (digits === currentLocal) {
      setEditMobileError("Enter a different mobile number to update.");
      return;
    }

    setEditMobileValue(formattedMobile);
    setIsSubmittingEditMobile(true);
    setEditMobileError("");
    setMobileSendError("");

    try {
      const result = await sendEditMobileOtp({
        emailId: getStoredUserEmail(),
        role: PROFILE_ROLE_CAREGIVER,
        mobileNumber: formattedMobile,
      });

      if (result.success) {
        setPendingEditMobile(formattedMobile);
        setMobileOtpMeta(result.data);
        setEditMobileOpen(false);
        setVerifyTarget("mobile");
        return;
      }

      if (isSendOtpRateLimitError(result.data)) {
        const lockEndsAt = parseLockedUntilMs(result.data?.lockedUntil) || (result.data?.attemptsRemaining === 0 ? Date.now() + RESEND_LOCK_MS : null);
        applyMobileSendLock(lockEndsAt);
        setEditMobileError(formatMobileSendLockMessage(Math.max(0, Math.ceil((lockEndsAt - Date.now()) / 1000))));
        return;
      }

      setEditMobileError(result.message || "Failed to send verification code.");
    } catch (error) {
      Sentry.captureException(error);
      setEditMobileError("Failed to send verification code.");
    } finally {
      setIsSubmittingEditMobile(false);
    }
  };

  const handleOtpSendRateLimited = (lockEndsAt) => {
    const target = verifyTarget;
    setVerifyTarget(null);
    setMobileOtpMeta(null);
    setEmailOtpMeta(null);
    setPendingEditMobile("");

    if (target === "mobile") {
      applyMobileSendLock(lockEndsAt);
      return;
    }

    if (target === "email") {
      applyEmailSendLock(lockEndsAt);
    }
  };

  const handleVerifySuccess = async (message) => {
    const target = verifyTarget;
    const editedMobile = pendingEditMobile;
    setVerifyTarget(null);
    setMobileOtpMeta(null);
    setEmailOtpMeta(null);
    setMobileSendError("");
    setEmailSendError("");
    clearPersistedMobileResendLock();
    clearPersistedEmailVerifyResendLock();

    if (target === "mobile" && editedMobile) {
      setPendingEditMobile("");
      markContactVerified(CONTACT_VERIFICATION_ROLE.CAREGIVER, "mobile");
      setProfile((prev) => {
        const updated = {
          ...prev,
          mobile: editedMobile,
          hasMobile: true,
          mobileVerified: true,
        };
        syncContactVerificationFromProfile(CONTACT_VERIFICATION_ROLE.CAREGIVER, updated);
        return updated;
      });
      setVerifyToast(message || "Mobile number updated successfully.");
      return;
    }

    setPendingEditMobile("");
    markContactVerified(CONTACT_VERIFICATION_ROLE.CAREGIVER, target);
    setProfile((prev) => {
      const updated = {
        ...prev,
        ...(target === "email" ? { emailVerified: true } : { mobileVerified: true }),
      };
      syncContactVerificationFromProfile(CONTACT_VERIFICATION_ROLE.CAREGIVER, updated);
      return updated;
    });

    if (target === "mobile") {
      setVerifyToast(message || "Mobile number verified successfully.");
    } else {
      setVerifyToast(message || "Email verified successfully.");
    }
  };

  useEffect(() => {
    const persistedLock = readPersistedMobileResendLock(profile.mobile);
    if (persistedLock) {
      setMobileSendLockEndsAt(persistedLock);
    }
  }, [profile.mobile]);

  useEffect(() => {
    const persistedLock = readPersistedEmailVerifyResendLock(profile.email);
    if (persistedLock) {
      setEmailSendLockEndsAt(persistedLock);
    }
  }, [profile.email]);

  useEffect(() => {
    if (!mobileSendLockEndsAt || mobileSendLockEndsAt <= Date.now()) {
      setMobileSendLockSeconds(0);
      return undefined;
    }

    const tick = () => {
      const remaining = Math.max(0, Math.ceil((mobileSendLockEndsAt - Date.now()) / 1000));
      setMobileSendLockSeconds(remaining);
      if (remaining === 0) {
        setMobileSendLockEndsAt(null);
        clearPersistedMobileResendLock();
      }
    };

    tick();
    const timerId = window.setInterval(tick, 1000);
    return () => window.clearInterval(timerId);
  }, [mobileSendLockEndsAt]);

  useEffect(() => {
    if (!emailSendLockEndsAt || emailSendLockEndsAt <= Date.now()) {
      setEmailSendLockSeconds(0);
      return undefined;
    }

    const tick = () => {
      const remaining = Math.max(0, Math.ceil((emailSendLockEndsAt - Date.now()) / 1000));
      setEmailSendLockSeconds(remaining);
      if (remaining === 0) {
        setEmailSendLockEndsAt(null);
        clearPersistedEmailVerifyResendLock();
      }
    };

    tick();
    const timerId = window.setInterval(tick, 1000);
    return () => window.clearInterval(timerId);
  }, [emailSendLockEndsAt]);

  useEffect(() => {
    let cancelled = false;

    const fetchProfile = async () => {
      const emailId = getStoredUserEmail();
      if (!emailId) {
        if (!cancelled) {
          setLoadError("Unable to load profile. Please sign in again.");
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      setLoadError("");
      const result = await getProfileDetails({ emailId, role: PROFILE_ROLE_CAREGIVER });

      if (cancelled) return;

      if (!result.success || !result.data) {
        setLoadError(result.message || "Failed to load profile details.");
        setLoading(false);
        return;
      }

      const nextProfile = mapApiCaregiverProfileToState(result.data);
      syncContactVerificationFromProfile(CONTACT_VERIFICATION_ROLE.CAREGIVER, nextProfile);
      setProfile(nextProfile);
      setLoading(false);
    };

    fetchProfile();
    return () => {
      cancelled = true;
    };
  }, []);

  const displayEmail = profile.email || "";
  const displayMobile = profile.mobile ? formatProfileMobile(profile.mobile) : "";
  const otpMobileNumber = pendingEditMobile || profile.mobile;
  const otpMobileDisplay = pendingEditMobile ? formatProfileMobile(pendingEditMobile) : displayMobile;
  const emailFieldWarning = emailSendLockMessage || emailSendError;
  const mobileFieldWarning = mobileSendLockMessage || mobileSendError;

  return (
    <div className="caregiver-page-layout caregiver-settings-layout">
      {verifyToast ? <AlertMessage status={200} message={verifyToast} origin="center" closeAlert={() => setVerifyToast("")} /> : null}
      <Header noBorderBottom />
      <Backdrop sx={{ color: "#ffba30", zIndex: (theme) => theme.zIndex.drawer + 1000 }} open={loading || isSubmittingEditMobile}>
        <img src={require("../../assets/btTree.png")} className="loading-img" alt="" />
      </Backdrop>

      <div className="caregiver-settings-page caregiver-profile-page">
        <div className="family-checklist-topbar">
          <button type="button" className="family-checklist-back" onClick={() => navigate(-1)} aria-label="Go back">
            <IoChevronBack />
          </button>
          <h1 className="family-checklist-title-center">Profile</h1>
          <span className="family-vitals-topbar-spacer" aria-hidden="true" />
        </div>

        {!loading && loadError ? (
          <p className="family-profile-error" role="alert">
            {loadError}
          </p>
        ) : null}

        <FamilyOtpVerifyDialog
          open={Boolean(verifyTarget)}
          onClose={() => {
            setVerifyTarget(null);
            setPendingEditMobile("");
          }}
          target={verifyTarget || "email"}
          email={profile.email}
          mobileNumber={otpMobileNumber}
          contactDisplay={verifyTarget === "mobile" ? otpMobileDisplay : displayEmail}
          verificationRole={CONTACT_VERIFICATION_ROLE.CAREGIVER}
          mode={pendingEditMobile ? OTP_MODE_EDIT_MOBILE : undefined}
          initialOtpMeta={verifyTarget === "mobile" ? mobileOtpMeta : verifyTarget === "email" ? emailOtpMeta : null}
          onSendRateLimited={handleOtpSendRateLimited}
          onSuccess={handleVerifySuccess}
        />

        <Dialog
          className="family-settings-verify-dialog"
          open={editMobileOpen}
          onClose={closeEditMobile}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            className: "family-settings-dialog-paper family-settings-verify-dialog-paper",
          }}>
          <DialogContent className="family-modal-content family-settings-edit-dialog-content">
            <div className="family-settings-edit-dialog-header">
              <h2 className="family-modal-title">Edit mobile number</h2>
              <button type="button" className="family-modal-close" onClick={closeEditMobile} aria-label="Dismiss">
                <IoClose />
              </button>
            </div>
            <form className="family-settings-edit-form" onSubmit={handleEditMobileSubmit}>
              <p className="family-settings-edit-desc">Enter a valid 10-digit mobile number.</p>
              <label htmlFor="cg-profile-edit-mobile" className="family-settings-otp-label">
                Mobile number
              </label>
              <input
                id="cg-profile-edit-mobile"
                type="tel"
                inputMode="numeric"
                className="family-settings-edit-input"
                value={editMobileValue}
                placeholder="xxx-yyy-zzzz"
                onChange={(e) => {
                  setEditMobileValue(formatEditMobileInput(e.target.value));
                  if (editMobileError) setEditMobileError("");
                }}
                maxLength={12}
                autoComplete="tel"
                disabled={isSubmittingEditMobile}
              />
              {editMobileError ? (
                <p className="family-settings-otp-error" role="alert">
                  {editMobileError}
                </p>
              ) : null}
              <button type="submit" className="family-settings-otp-submit" disabled={isSubmittingEditMobile}>
                {isSubmittingEditMobile ? "Sending…" : "Send OTP"}
              </button>
            </form>
          </DialogContent>
        </Dialog>

        {!loading && !loadError ? (
          <>
            <ProfileHero profile={profile} />

            <section className="family-profile-form" aria-labelledby="cg-profile-contact-heading">
              <h3 id="cg-profile-contact-heading" className="family-profile-section-title">
                Contact
              </h3>
              <div className="family-profile-form-card">
                <ContactFormField
                  id="cg-profile-email"
                  label="Email"
                  icon={IoMailOutline}
                  value={displayEmail || "—"}
                  isVerified={profile.emailVerified}
                  hasValue={Boolean(displayEmail)}
                  onVerify={handleEmailVerify}
                  verifyDisabled={isEmailSendLocked}
                  isVerifying={isSendingEmailOtp}
                  fieldWarning={emailFieldWarning}
                />
                <ContactFormField
                  id="cg-profile-mobile"
                  label="Mobile number"
                  icon={IoCallOutline}
                  value={displayMobile || "—"}
                  isVerified={profile.mobileVerified}
                  hasValue={Boolean(displayMobile)}
                  onVerify={handleMobileVerify}
                  onEdit={!profile.mobileVerified ? openEditMobile : undefined}
                  verifyDisabled={isMobileSendLocked}
                  isVerifying={isSendingMobileOtp}
                  fieldWarning={mobileFieldWarning}
                />
              </div>
            </section>

            <section className="family-profile-form" aria-labelledby="cg-profile-security-heading">
              <h3 id="cg-profile-security-heading" className="family-profile-section-title">
                Security
              </h3>
              <button type="button" className="family-profile-security-row" onClick={() => navigate("/caregiver-reset-password")}>
                <span className="family-profile-security-icon" aria-hidden="true">
                  <IoLockClosedOutline />
                </span>
                <span className="family-profile-security-copy">
                  <span className="family-profile-security-label">Reset login code</span>
                  <span className="family-profile-security-hint">Change your 4-digit login code</span>
                </span>
                <IoChevronForward className="family-profile-security-chevron" aria-hidden="true" />
              </button>
            </section>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default CaregiverProfile;
